import {useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {TopSlidesAnalyticsEnum} from '../../../incase-domain/proptypes/slidesAnalytics';
import {
    fetchMostViewedSlidesAnalytics,
    fetchPresAnalytics,
    fetchPresSlidesTopTimeAnalytics,
    invalidateAnalyticsCache,
} from '../../../service/analytics';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import usePresentationToEdit from '../../hooks/usePresentationToEdit';
import InteractionsCalendar from './components/InteractionsCalendar';
import LeaderboardReport from './components/LeaderboardReport';
import MostViewedSlides from './components/MostViewedSlides';
import PresentationQuestions from './components/PresentationQuestions';
import ReportsFilter from './components/ReportsFilter';
import SlideClicksReport from './components/SlideClicksReport';
import TextInputsReport from './components/TextInputsReport';
import UserInteraction from './components/UserInteraction';
import ReportsHeader from './components/ReportsHeader';
import AveragePointsReport from './components/AveragePointsReport';
import SlideTimeReport from './components/SlideTimeReport';
import PdfReportFiltersHeader from './components/PdfReportFiltersHeader';

function PresentationReportsPage(): JSX.Element {
    const {t} = useTranslation();

    const selectedPresentation = useSelector(selectedPresentationSelector);
    const client = useQueryClient();
    const reportsRef = useRef(null);

    const match = useMatch('presentations/:presentationId/report');
    const presId = match?.params.presentationId ?? selectedPresentation._id;
    usePresentationToEdit(presId);

    const [filterQuery, setFilterQuery] = useState({});
    const [slideToViewID, setSlideToViewID] = useState<string | undefined>();

    const {
        data: analytics,
        isLoading: analyticsIsLoading,
        isFetching: analyticsIsFetching,
    } = useQuery(QUERY_KEYS.analytics.base(presId, filterQuery), () => fetchPresAnalytics(presId, filterQuery), {
        keepPreviousData: true,
    });
    const {
        data: mostViewedSlides,
        isLoading: mostViewedSlidesIsLoading,
        isFetching: mostViewedSlidesIsFetching,
    } = useQuery(
        QUERY_KEYS.analytics.topSlides(presId, filterQuery),
        () => fetchMostViewedSlidesAnalytics(presId, TopSlidesAnalyticsEnum.ALL, filterQuery),
        {
            keepPreviousData: true,
        }
    );

    const {data: slidesTimeAnalytics} = useQuery(
        QUERY_KEYS.analytics.topTime(presId, filterQuery),
        () => fetchPresSlidesTopTimeAnalytics(presId, filterQuery),
        {
            keepPreviousData: true,
        }
    );

    function resetCache(): void {
        invalidateAnalyticsCache(presId).then((res) => {
            if (res.success) {
                client.invalidateQueries({queryKey: QUERY_KEYS.analytics.all});
            }
        });
    }

    const isFetching = analyticsIsFetching || mostViewedSlidesIsFetching;
    const isLoading = analyticsIsLoading || mostViewedSlidesIsLoading;

    return (
        <div className="p-presEdit">
            <div className="l-container p-presReport">
                <ReportsHeader onClickRefresh={resetCache} filterQuery={filterQuery} reportsRef={reportsRef} />
                {/* <ReportsFilter setFilterQuery={setFilterQuery} /> */}

                <div className="p-presReport__body">
                    <aside className="p-presReport__body_sidebar">
                        <div>
                            <div className="p-presReport__card_title">Filters</div>
                            <div className="p-presReport__card">
                                <ReportsFilter setFilterQuery={setFilterQuery} />
                            </div>
                        </div>
                    </aside>

                    <div className="p-presReport__body_main">
                        <UserInteraction userInteractions={analytics?.userInteractions} isLoading={isLoading || isFetching} />
                        <InteractionsCalendar calendarAnalytics={analytics?.calendarAnalytics} isLoading={isLoading || isFetching} />
                        <AveragePointsReport analytics={analytics?.questionsTotalAnalytics} isLoading={isLoading || isFetching} />

                        {analytics?.questionReport && analytics.questionReport.length > 0 && (
                            <PresentationQuestions
                                questionReport={analytics?.questionReport}
                                presentationId={presId}
                                slideToViewID={slideToViewID}
                                setSlideToViewID={setSlideToViewID}
                                isLoading={isLoading || isFetching}
                            />
                        )}

                        {analytics?.textInputs && analytics?.textInputs?.length > 0 && (
                            <TextInputsReport textInputs={analytics?.textInputs} isLoading={isLoading || isFetching} />
                        )}

                        <div>
                            <div className="p-presReport__card_title">{t('reportPage.slideStatistics')}</div>
                            <div className="p-presReport__card">
                                <div className="u-flex u-flex-col u-gap-8">
                                    {mostViewedSlides && (
                                        <MostViewedSlides
                                            selectedPresentation={selectedPresentation}
                                            mostViewedSlides={mostViewedSlides}
                                            setSlideToViewID={setSlideToViewID}
                                            isLoading={isLoading || isFetching}
                                        />
                                    )}
                                    {analytics?.slideClicks && (
                                        <SlideClicksReport
                                            selectedPresentation={selectedPresentation}
                                            slideClicks={analytics.slideClicks}
                                            isLoading={isLoading || isFetching}
                                        />
                                    )}
                                    {slidesTimeAnalytics && (
                                        <SlideTimeReport
                                            selectedPresentation={selectedPresentation}
                                            slideAnalytics={slidesTimeAnalytics}
                                            isLoading={isLoading || isFetching}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-presReport__leaderboard">
                            <LeaderboardReport
                                title={t('reportPage.leaderboardTitleUsers')}
                                leaderboard={analytics?.leaderboard?.specificLeaderboard}
                                isLoading={isLoading || isFetching}
                            />

                            <LeaderboardReport
                                title={t('reportPage.leaderboardTitleGroups')}
                                leaderboard={analytics?.leaderboard?.groupLeaderboard}
                                isLoading={isLoading || isFetching}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* report to pdf body */}
            <div style={{visibility: 'hidden', maxHeight: 0, overflow: 'hidden'}}>
                <div id="reportPdf" className="p-presReport u-pt-5" ref={reportsRef}>
                    <PdfReportFiltersHeader filterQuery={filterQuery} />
                    <UserInteraction userInteractions={analytics?.userInteractions} isLoading={isLoading} />
                    <InteractionsCalendar calendarAnalytics={analytics?.calendarAnalytics} isLoading={isLoading} />
                    <AveragePointsReport analytics={analytics?.questionsTotalAnalytics} isLoading={isLoading || isFetching} />

                    {analytics?.questionReport && analytics.questionReport.length > 0 && (
                        <PresentationQuestions
                            questionReport={analytics?.questionReport}
                            presentationId={presId}
                            slideToViewID={slideToViewID}
                            setSlideToViewID={setSlideToViewID}
                            isLoading={isLoading || isFetching}
                            openAll
                            disablePercentageWidth
                        />
                    )}

                    {analytics?.textInputs && analytics?.textInputs?.length > 0 && (
                        <TextInputsReport textInputs={analytics?.textInputs} isLoading={isLoading || isFetching} />
                    )}

                    <div>
                        <div className="p-presReport__card_title">{t('reportPage.slideStatistics')}</div>
                        <div className="p-presReport__card">
                            <div className="u-flex u-flex-col u-gap-8">
                                {mostViewedSlides && (
                                    <MostViewedSlides
                                        selectedPresentation={selectedPresentation}
                                        mostViewedSlides={mostViewedSlides}
                                        setSlideToViewID={setSlideToViewID}
                                        isLoading={isLoading || isFetching}
                                    />
                                )}
                                {analytics?.slideClicks && (
                                    <SlideClicksReport
                                        selectedPresentation={selectedPresentation}
                                        slideClicks={analytics.slideClicks}
                                        isLoading={isLoading || isFetching}
                                    />
                                )}
                                {slidesTimeAnalytics && (
                                    <SlideTimeReport
                                        selectedPresentation={selectedPresentation}
                                        slideAnalytics={slidesTimeAnalytics}
                                        isLoading={isLoading || isFetching}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-presReport__leaderboard">
                        <LeaderboardReport
                            title={t('reportPage.leaderboardTitleUsers')}
                            leaderboard={analytics?.leaderboard?.specificLeaderboard}
                            pdfLayout
                            isLoading={isLoading || isFetching}
                        />
                        <LeaderboardReport
                            title={t('reportPage.leaderboardTitleGroups')}
                            leaderboard={analytics?.leaderboard?.groupLeaderboard}
                            pdfLayout
                            isLoading={isLoading || isFetching}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PresentationReportsPage;
